import React from 'react';
import { useQuery } from 'react-query';
import toastr from 'react-hot-toast';
import { SPARK_RESOURCE_TYPES } from 'constants/common';
import { CACHE_AND_STALE_TIME } from 'constants/index';
import {
  generateFilter,
  generateIncludeQuery,
  generateURL,
} from 'utilities/hooks/urlGeneration';
import { fetchWrapper } from 'utils/fetchWrapper';
import { filterIncludedItem } from 'utilities/hooks/responseHandlers';
import { useSparkStepTypes } from 'hooks/EntityTypes/useSparkStepTypes';
import { numericSort } from 'utilities/sort';
import type { SparkTaskResponse, SparkTask } from 'types/SparkTasks';
import type { SparkStep } from 'types/SparkStep';
import type { SparkStepType } from 'types/entityTypes/SparkStepType';
import type { PromptFunctions } from 'types/PromptFunctions';

export type JoinedRunTasks = {
  sparkStep: SparkStep | undefined;
  stepType: SparkStepType | undefined;
} & SparkTask;

const getSparkRunTaskByRunID = (runID: string | undefined) => {
  if (!runID) {
    Promise.reject('Spark Run ID cannot be null');
    return;
  }
  const queryArray = [
    generateFilter('sparkRunID', runID),
    generateIncludeQuery(['sparkStep', 'sparkStep.promptFunction']),
  ];
  const url = generateURL(SPARK_RESOURCE_TYPES.SPARK_TASKS, queryArray);
  return fetchWrapper<SparkTaskResponse>({
    url: url,
    method: 'GET',
  });
};

export const useSparkRunTaskByRunID = (runID: string | undefined) => {
  const {
    idMap: sparkStepTypeIdMap,
    isLoading,
    isFetching,
  } = useSparkStepTypes();
  const response = useQuery(
    [SPARK_RESOURCE_TYPES.SPARK_TASKS, runID],
    () => getSparkRunTaskByRunID(runID),
    {
      onError: (error) => {
        if (error instanceof Error) {
          toastr.error(`Failed to fetch Spark Task: ${error.message}`);
        }
      },
      enabled: Boolean(runID),
      ...CACHE_AND_STALE_TIME,
    }
  );
  const { data } = response;
  const { data: sparkTasks, included } = data || {};

  const sparkSteps = filterIncludedItem<SparkStep>(
    included,
    SPARK_RESOURCE_TYPES.SPARK_STEPS
  );

  const promptFunctions = filterIncludedItem<PromptFunctions>(
    included,
    SPARK_RESOURCE_TYPES.PROMPT_FUNCTIONS
  );

  const promptFunctionByStepID = React.useMemo<
    Record<string, PromptFunctions | undefined>
  >(() => {
    if (!sparkSteps || !promptFunctions) return {};

    const promptFunctionMap = promptFunctions.reduce<
      Record<string, PromptFunctions>
    >((map, func) => {
      map[func.id] = func;
      return map;
    }, {});

    return sparkSteps
      .filter((step) => !!step.attributes.promptFunctionID)
      .reduce(
        (acc, step) => {
          const promptFunction =
            promptFunctionMap[step.attributes.promptFunctionID + ''];
          acc[step.id] = promptFunction;
          return acc;
        },
        {} as Record<string, PromptFunctions | undefined>
      );
  }, [promptFunctions, sparkSteps]);

  const joinedRunTasks: JoinedRunTasks[] | undefined = sparkTasks?.map(
    (task) => {
      const sparkStep = sparkSteps?.find(
        (step) => task.attributes.sparkStepID === step.id
      );
      return {
        ...task,
        sparkStep,
        stepType:
          sparkStepTypeIdMap?.[sparkStep?.attributes.sparkStepTypeID || ''],
      };
    }
  );

  const sortedJoinedSparkTasks = joinedRunTasks?.sort((a, b) =>
    numericSort(
      a.sparkStep?.attributes.stepOrder,
      b.sparkStep?.attributes.stepOrder
    )
  );

  return {
    ...response,
    sparkTasks,
    promptFunctionByStepID,
    joinedRunTasks: sortedJoinedSparkTasks,
    isLoading: response.isLoading || isLoading,
    isFetching: response.isFetching || isFetching,
  };
};
